import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { RefObject } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    ArrowRight,
    BookOpen,
    Clock,
    Play,
    Sparkles,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
    metaChipClass,
    paperCardClass,
    slateSurfaceClass,
} from "@/lib/card-styles";
import {
    typeBodyClass,
    typeHeadwordClass,
    typeMetaClass,
    typeNumericClass,
} from "@/lib/typography";
import type { VideoReviewItem } from "@/lib/video-types";
import { getPlatformDisplayName, getVideoThumbnail } from "@/lib/video-types";
import { Mascot, useMascotMood } from "@/components/mascot";
import type { DueVocabularyItem } from "@/hooks/use-review-reminder";

interface ReviewReminderModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    vocabularyDue: number;
    /** Due words themselves — the modal shows headwords, not just a count. */
    vocabularyDueItems: DueVocabularyItem[];
    videosDue: VideoReviewItem[];
    onDismiss: () => void;
}

type QueueTab = "words" | "videos";

/** How many rows to show before collapsing into a "+N more" summary. */
const WORD_PREVIEW = 6;
const VIDEO_PREVIEW = 4;

/** Keep ~5s of attention per card, floored at one minute. */
const estimateMinutes = (count: number) => Math.max(1, Math.ceil((count * 5) / 60));

/**
 * Bottom sheet on a phone, centred card from `sm` up. Shared by both states
 * below so the celebration can't drift away from the reminder's geometry.
 */
const MODAL_CLASS = cn(
    "p-0 gap-0 overflow-hidden flex flex-col",
    "max-h-[90vh] sm:max-h-[85vh]",
    "max-w-none sm:max-w-md",
    "left-0 right-0 bottom-0 top-auto translate-x-0 translate-y-0",
    "rounded-t-2xl rounded-b-none border-0 sm:border",
    "data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom",
    "data-[state=open]:zoom-in-100 data-[state=closed]:zoom-out-100",
    "data-[state=open]:slide-in-from-left-0 data-[state=closed]:slide-out-to-left-0",
    "duration-300",
    "sm:left-[50%] sm:top-[50%] sm:bottom-auto sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-lg",
);

function formatLastPracticed(lastPracticedAt: string | null, t: TFunction): string {
    if (!lastPracticedAt) return t("pages.review.notPracticed", "Not practiced yet");
    const diffDays = Math.floor((Date.now() - new Date(lastPracticedAt).getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays <= 0) return t("pages.review.today", "Today");
    if (diffDays === 1) return t("pages.review.yesterday", "Yesterday");
    if (diffDays < 7) return t("pages.review.daysAgo_other", { count: diffDays, defaultValue: "{{count}} days ago" });
    if (diffDays < 14) return t("pages.review.weekAgo", "1 week ago");
    if (diffDays < 30) return t("pages.review.weeksAgo_other", { count: Math.floor(diffDays / 7), defaultValue: "{{count}} weeks ago" });
    if (diffDays < 60) return t("pages.review.monthAgo", "1 month ago");
    return t("pages.review.monthsAgo_other", { count: Math.floor(diffDays / 30), defaultValue: "{{count}} months ago" });
}

/** "3 days late" → a small amber chip. "Due today" stays quiet. */
function OverdueChip({ daysOverdue, t }: { daysOverdue: number; t: TFunction }) {
    if (daysOverdue <= 0) {
        return (
            <span className={cn(typeMetaClass, "whitespace-nowrap text-[10px] text-stone-500 dark:text-stone-400")}>
                {t("pages.review.dueToday", "Due today")}
            </span>
        );
    }
    return (
        <span
            className={cn(
                metaChipClass,
                "whitespace-nowrap !px-2 !py-0.5 !text-[10px] uppercase tracking-wider",
                "bg-amber-50 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
            )}
        >
            <Clock className="h-2.5 w-2.5" aria-hidden="true" />
            {t("pages.review.daysLate_other", { count: daysOverdue, defaultValue: daysOverdue === 1 ? "{{count}} day late" : "{{count}} days late" })}
        </span>
    );
}

const stagger = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.05, delayChildren: 0.1 } },
};

const fadeUp = {
    hidden: { y: 12, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { type: "spring" as const, stiffness: 340, damping: 28 } },
};

/**
 * Daily review reminder.
 *
 * The old version was two cards — vocabulary here, videos there — each with its
 * own button, which meant two competing actions and a screen where the learner
 * had to choose before they had any context. It also carried `vocabularyDueIds`
 * and never used them, so all the user ever saw was a number.
 *
 * The rebuilt version follows the same page contract as the rest of the app:
 *
 *   1. **Task header** — Lydi plus one serif line. No badge, no competing
 *      title/subtitle pair.
 *   2. **One count band** — the total is the primary figure; words, videos and
 *      the time estimate are quiet supporting counts behind hairlines.
 *   3. **Primary** — the actual queue, split by tab so one panel mounts at a
 *      time. Words render as index cards with real headwords, because that is
 *      the thing worth remembering when the interruption arrives.
 *   4. **Footer** — one primary CTA, which follows the active tab, plus a quiet
 *      "Not now".
 *
 * Warm paper surfaces, one blue action, serif reserved for the words being
 * learned.
 */
export const ReviewReminderModal = ({
    open,
    onOpenChange,
    vocabularyDue,
    vocabularyDueItems,
    videosDue,
    onDismiss,
}: ReviewReminderModalProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const hasVocabulary = vocabularyDue > 0;
    const hasVideos = videosDue.length > 0;
    const totalDue = vocabularyDue + videosDue.length;

    // Words are the default queue whenever there are any — vocabulary is the
    // larger, higher-value queue, so videos never hijacks the opening view.
    // Reset on open *and* whenever the available queues change, so a refetch
    // that adds or removes a queue can't strand the tab on an empty panel.
    const [tab, setTab] = useState<QueueTab>("words");
    useEffect(() => {
        setTab(hasVocabulary ? "words" : "videos");
    }, [open, hasVocabulary, hasVideos]);

    useEffect(() => {
        if (!open) return;
        const prevX = document.documentElement.style.overflowX;
        const prevBody = document.body.style.overflowX;
        document.documentElement.style.overflowX = "hidden";
        document.body.style.overflowX = "hidden";
        return () => {
            document.documentElement.style.overflowX = prevX;
            document.body.style.overflowX = prevBody;
        };
    }, [open]);

    const { mood: mascotMood, pulse: pulseMascot } = useMascotMood({ baseMood: "idle" });

    // `pulse` is a stable useCallback; depending on the whole `mascot` result
    // object instead would re-run this on every render and loop, because
    // pulsing sets state.
    useEffect(() => {
        if (!open) return;
        if (hasVocabulary || hasVideos) pulseMascot("good", 1200);
        else pulseMascot("perfect");
    }, [open, hasVocabulary, hasVideos, pulseMascot]);

    const words = useMemo(() => vocabularyDueItems.slice(0, WORD_PREVIEW), [vocabularyDueItems]);
    const wordOverflow = Math.max(0, vocabularyDue - words.length);
    const videos = useMemo(() => videosDue.slice(0, VIDEO_PREVIEW), [videosDue]);
    const videoOverflow = Math.max(0, videosDue.length - videos.length);

    /**
     * Radix focuses the first tabbable element when the dialog opens, which was
     * landing on the default queue tab — Enter then re-selected the tab that was
     * already active, and a `:focus-visible` ring drew attention to an
     * arbitrary-looking control. Send focus to the one primary action instead.
     */
    const primaryActionRef = useRef<HTMLButtonElement>(null);
    const onOpenAutoFocus = (event: Event) => {
        event.preventDefault();
        primaryActionRef.current?.focus();
    };

    const summary = useMemo(() => {
        const parts: string[] = [];
        if (hasVocabulary) parts.push(t("common.count.word", { count: vocabularyDue, defaultValue: vocabularyDue === 1 ? "{{count}} word" : "{{count}} words" }));
        if (hasVideos) parts.push(t("common.count.video", { count: videosDue.length, defaultValue: videosDue.length === 1 ? "{{count}} video" : "{{count}} videos" }));
        if (!parts.length) return t("pages.review.nothingWaiting", "Nothing is waiting on you right now.");
        const minutes = estimateMinutes(totalDue);
        return t("pages.review.queueSummary", {
            items: parts.join(t("pages.review.listSeparator", " and ")),
            minutes,
            defaultValue: "{{items}} are ready. About {{minutes}} minute of practice.",
        });
    }, [hasVocabulary, hasVideos, vocabularyDue, videosDue.length, totalDue, t]);

    // The count band drops a column rather than showing an em-dash: a "Videos
    // —" cell on a words-only queue is a fact nobody needed.
    const counts = useMemo(() => {
        const cells: { key: string; label: string; value: string; primary?: boolean }[] = [
            { key: "total", label: t("pages.review.dueNow", "Due now"), value: String(totalDue), primary: true },
        ];
        if (hasVocabulary) cells.push({ key: "words", label: t("pages.review.words", "Words"), value: String(vocabularyDue) });
        if (hasVideos) cells.push({ key: "videos", label: t("pages.review.videos", "Videos"), value: String(videosDue.length) });
        cells.push({ key: "estimate", label: t("pages.review.estimate", "Est."), value: `${estimateMinutes(totalDue)}m` });
        return cells;
    }, [totalDue, hasVocabulary, hasVideos, vocabularyDue, videosDue.length, t]);

    const handleDismissAndClose = () => {
        onDismiss();
        onOpenChange(false);
    };

    const handleStartVocabularyReview = () => {
        onDismiss();
        onOpenChange(false);
        navigate("/vocabulary/practice?mode=due");
    };

    const handleOpenVideos = () => {
        onDismiss();
        onOpenChange(false);
        navigate("/videos");
    };

    // One CTA, and it follows whichever queue is on screen.
    const activeTab: QueueTab = hasVocabulary ? tab : "videos";
    const cta = activeTab === "words"
        ? { label: t("pages.review.reviewWords", { count: vocabularyDue, defaultValue: "Review {{count}} words" }), icon: BookOpen, onClick: handleStartVocabularyReview }
        : { label: t("pages.review.practiceVideos", { count: videosDue.length, defaultValue: "Practice {{count}} videos" }), icon: Play, onClick: handleOpenVideos };
    const CtaIcon = cta.icon;

    if (!hasVocabulary && !hasVideos) {
        return (
            <Dialog open={open} onOpenChange={(isOpen) => {
                if (!isOpen) onDismiss();
                onOpenChange(isOpen);
            }}>
                <DialogContent
                    onOpenAutoFocus={onOpenAutoFocus}
                    className={MODAL_CLASS}
                >
                    <CelebrationState onClose={handleDismissAndClose} buttonRef={primaryActionRef} />
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onOpenChange={(isOpen) => {
            if (!isOpen) {
                onDismiss();
            }
            onOpenChange(isOpen);
        }}>
            <DialogContent
                onOpenAutoFocus={onOpenAutoFocus}
                className={cn(MODAL_CLASS, "md:max-w-lg")}
            >
                <div className="flex flex-col flex-1 overflow-hidden bg-background">

                    {/* ── 1. Task header — Lydi plus one line ─────────────── */}
                    <div
                        className={cn(
                            "relative shrink-0 overflow-hidden",
                            "bg-gradient-to-br from-stone-50 via-background to-amber-50/40",
                            "dark:from-stone-900/50 dark:via-background dark:to-amber-950/15"
                        )}
                    >
                        {/* Dot texture on the right, masked so it fades out. */}
                        <div
                            aria-hidden="true"
                            className="pointer-events-none absolute inset-y-0 right-0 w-2/5 opacity-40 dark:opacity-20"
                            style={{
                                backgroundImage:
                                    "radial-gradient(circle at 1px 1px, hsl(var(--muted-foreground) / 0.22) 1px, transparent 0)",
                                backgroundSize: "14px 14px",
                                maskImage: "linear-gradient(to right, transparent, black 55%)",
                                WebkitMaskImage: "linear-gradient(to right, transparent, black 55%)",
                            }}
                        />

                        {/* Mobile drag handle */}
                        <div className="relative flex justify-center pt-2 sm:hidden">
                            <div className="h-1 w-9 rounded-full bg-muted-foreground/20" />
                        </div>

                        <div className="relative flex items-start gap-4 px-5 pb-3.5 pt-3 pr-14 sm:px-6 sm:pb-4 sm:pt-4">
                            <motion.div
                                initial={{ scale: 0, rotate: -12, opacity: 0 }}
                                animate={{ scale: 1, rotate: 0, opacity: 1 }}
                                transition={{ type: "spring", stiffness: 320, damping: 18, delay: 0.04 }}
                                className="shrink-0"
                            >
                                <div className="rounded-full bg-white/70 p-1 shadow-sm ring-1 ring-stone-200/70 dark:bg-stone-900/60 dark:ring-stone-800">
                                    <Mascot
                                        mood={mascotMood}
                                        size="md"
                                        ariaLabel={`Lydi says: ${summary}`}
                                    />
                                </div>
                            </motion.div>

                            <div className="min-w-0 flex-1">
                                <span className={cn(metaChipClass, "uppercase tracking-wider")}>
                                    <Sparkles className="h-3 w-3 text-amber-500" aria-hidden="true" />
                                    {t("pages.review.dailyReview", "Daily review")}
                                </span>
                                <DialogTitle className="mt-2 font-display text-2xl font-semibold tracking-tight text-left leading-tight text-stone-900 dark:text-stone-50">
                                    {t("pages.review.timeToReview", "Time to review")}
                                </DialogTitle>
                                <DialogDescription className={cn(typeBodyClass, "mt-1.5 text-left")}>
                                    {summary}
                                </DialogDescription>
                            </div>
                        </div>
                    </div>

                    {/* ── 2. One count band — total leads, the rest are quiet ── */}
                    <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1, duration: 0.25 }}
                        className="shrink-0 px-5 pt-3.5 sm:px-6"
                    >
                        <div
                            className={cn(
                                slateSurfaceClass,
                                "flex items-stretch !rounded-xl !bg-stone-50/80 !p-0 dark:!bg-stone-800/40"
                            )}
                        >
                            {counts.map(({ key, label, value, primary }, i) => (
                                <Fragment key={key}>
                                    {i > 0 && (
                                        <div
                                            aria-hidden="true"
                                            className="w-px shrink-0 self-stretch bg-stone-200/70 dark:bg-stone-800"
                                        />
                                    )}
                                    <div className="flex flex-1 flex-col justify-center gap-1 px-3 py-3">
                                        <span
                                            className={cn(
                                                typeNumericClass,
                                                "inline-flex items-center gap-1 font-bold leading-none",
                                                primary
                                                    ? "text-2xl text-stone-900 dark:text-stone-50"
                                                    : "text-base text-stone-600 dark:text-stone-300"
                                            )}
                                        >
                                            {key === "estimate" && (
                                                <Clock className="h-3.5 w-3.5 text-stone-400" aria-hidden="true" />
                                            )}
                                            {value}
                                        </span>
                                        <span className={cn(typeMetaClass, "whitespace-nowrap !text-[10px]")}>{label}</span>
                                    </div>
                                </Fragment>
                            ))}
                        </div>
                    </motion.div>

                    {/* ── 3. Primary — the queue itself ────────────────────── */}
                    <div className="shrink-0 px-5 pt-4 sm:px-6">
                        {hasVocabulary && hasVideos ? (
                            <div
                                role="tablist"
                                aria-label={t("pages.dialogs.reviewQueue", "Review queue")}
                                className="flex gap-1 rounded-xl bg-stone-100 p-1 dark:bg-stone-800/70"
                            >
                                {([
                                    { key: "words" as const, label: t("pages.review.words", "Words"), count: vocabularyDue, Icon: BookOpen },
                                    { key: "videos" as const, label: t("pages.review.videos", "Videos"), count: videosDue.length, Icon: Play },
                                ]).map(({ key, label, count, Icon }) => {
                                    const isActive = activeTab === key;
                                    return (
                                        <button
                                            key={key}
                                            role="tab"
                                            aria-selected={isActive}
                                            onClick={() => setTab(key)}
                                            className={cn(
                                                "flex h-8 flex-1 items-center justify-center gap-1.5 rounded-lg text-xs font-semibold transition-all duration-200",
                                                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                                                isActive
                                                    ? "bg-white text-stone-900 shadow-sm dark:bg-stone-900 dark:text-stone-50"
                                                    : "text-stone-500 hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-200"
                                            )}
                                        >
                                            <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                                            {label}
                                            <span className={cn(typeNumericClass, "text-[11px] opacity-60")}>{count}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        ) : (
                            <p className={cn(typeMetaClass, "flex items-center gap-1.5")}>
                                {hasVocabulary
                                    ? <><BookOpen className="h-3 w-3" aria-hidden="true" />{t("pages.review.wordsReady", "Words ready")}</>
                                    : <><Play className="h-3 w-3" aria-hidden="true" />{t("pages.review.videosToRevisit", "Videos to revisit")}</>}
                            </p>
                        )}
                    </div>

                    {/* Plain overflow, not Radix ScrollArea: its viewport lays
                        the inner block out as a table, so `min-w-0` on the rows
                        never gets a chance to clamp and a nowrap chip pushed
                        the whole list wider than the dialog on a 390px screen.
                        The fade mask below reads as "there is more down here". */}
                    <div
                        className="mt-2.5 min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-5 pb-3 sm:px-6"
                        style={{
                            maskImage: "linear-gradient(to bottom, black calc(100% - 20px), transparent)",
                            WebkitMaskImage: "linear-gradient(to bottom, black calc(100% - 20px), transparent)",
                        }}
                    >
                        <motion.ul
                            key={activeTab}
                            className="space-y-2 pb-1"
                            variants={stagger}
                            initial="hidden"
                            animate="visible"
                        >
                            {activeTab === "words"
                                ? words.map((item) => <WordRow key={item.id} item={item} />)
                                : videos.map((video) => (
                                    <VideoRow
                                        key={video.videoId}
                                        video={video}
                                        onSelect={handleDismissAndClose}
                                    />
                                ))}
                        </motion.ul>

                        {activeTab === "words" && wordOverflow > 0 && (
                            <Link
                                to="/vocabulary/practice?mode=due"
                                onClick={handleStartVocabularyReview}
                                className="mt-2 block rounded-xl border border-dashed border-stone-300/80 px-3 py-2 text-center text-xs font-medium text-stone-500 transition-colors hover:border-primary/40 hover:text-primary dark:border-stone-700 dark:text-stone-400"
                            >
                                {t("pages.review.moreWaiting", { count: wordOverflow, defaultValue: "+{{count}} more words waiting" })}
                            </Link>
                        )}
                        {activeTab === "videos" && videoOverflow > 0 && (
                            <Link
                                to="/videos"
                                onClick={handleDismissAndClose}
                                className="mt-2 block rounded-xl border border-dashed border-stone-300/80 px-3 py-2 text-center text-xs font-medium text-stone-500 transition-colors hover:border-primary/40 hover:text-primary dark:border-stone-700 dark:text-stone-400"
                            >
                                +{videoOverflow} more in your library
                            </Link>
                        )}
                    </div>

                    {/* ── 4. Footer — one CTA, one quiet exit ───────────────── */}
                    <div className="shrink-0 space-y-1.5 border-t border-stone-200/70 bg-stone-50/50 px-5 py-3 dark:border-stone-800 dark:bg-stone-900/40 sm:px-6">
                        <button
                            ref={primaryActionRef}
                            onClick={cta.onClick}
                            className="flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-primary-strong text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        >
                            <CtaIcon className="h-4 w-4" aria-hidden="true" />
                            {cta.label}
                            <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                        </button>
                        <button
                            onClick={handleDismissAndClose}
                            className="w-full py-1 text-xs font-medium text-stone-500 transition-colors hover:text-stone-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:text-stone-400 dark:hover:text-stone-300"
                        >
                            {t("pages.review.notNow", "Not now — remind me later today")}
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

/**
 * One due word, rendered as an index card: a hairline left stripe, the word
 * set in the display serif because it is the subject matter, and its
 * definition underneath so the row is recognisable at a glance rather than a
 * wall of bare nouns.
 */
function WordRow({ item }: { item: DueVocabularyItem }) {
    const { t } = useTranslation();
    return (
        <motion.li variants={fadeUp} className={cn(paperCardClass, "flex items-center gap-3 overflow-hidden !py-2.5 pl-3 pr-3")}>
            <span className="h-8 w-0.5 shrink-0 rounded-full bg-gradient-to-b from-blue-400 to-blue-500" aria-hidden="true" />
            <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                    <span className={cn(typeHeadwordClass, "truncate !text-base")}>{item.word}</span>
                    {item.certLevel && (
                        <span className={cn(typeMetaClass, "shrink-0 !text-[10px]")}>{item.certLevel}</span>
                    )}
                </div>
                {item.definition && (
                    <p className="truncate text-xs text-stone-500 dark:text-stone-400">{item.definition}</p>
                )}
            </div>
            <OverdueChip daysOverdue={item.daysOverdue} t={t} />
        </motion.li>
    );
}

/** One video to revisit — thumbnail, title, platform, and how long ago. */
function VideoRow({ video, onSelect }: { video: VideoReviewItem; onSelect: () => void }) {
    const { t } = useTranslation();
    return (
        <motion.li variants={fadeUp}>
            <Link
                to={`/videos/${video.videoId}`}
                onClick={onSelect}
                className={cn(
                    paperCardClass,
                    "group flex items-center gap-3 p-2 transition-colors hover:border-primary/40 hover:bg-stone-50 dark:hover:bg-stone-800/40"
                )}
            >
                <span className="relative h-11 w-[4.5rem] shrink-0 overflow-hidden rounded-lg bg-stone-200 dark:bg-stone-800">
                    <VideoThumb video={video} />
                    <span
                        className="absolute inset-0 flex items-center justify-center bg-stone-900/25 opacity-0 transition-opacity group-hover:opacity-100"
                        aria-hidden="true"
                    >
                        <Play className="h-4 w-4 fill-white text-white" />
                    </span>
                </span>
                <span className="min-w-0 flex-1">
                    <span className="line-clamp-2 block text-sm font-medium leading-snug text-stone-900 dark:text-stone-100">
                        {video.title}
                    </span>
                    <span className="mt-0.5 flex items-center gap-1.5 text-[11px] text-stone-500 dark:text-stone-400">
                        <span className="font-medium">{getPlatformDisplayName(video.platform)}</span>
                        <span aria-hidden="true">·</span>
                        <span className="truncate">{formatLastPracticed(video.lastPracticedAt, t)}</span>
                    </span>
                </span>
            </Link>
        </motion.li>
    );
}

/**
 * Poster frame for a video row.
 *
 * YouTube thumbnails are fetched from the platform rather than stored, and a
 * video whose poster has since been removed resolves to a 404 — which Chrome
 * paints as a broken-image glyph in the middle of the card. Falling back to the
 * play placeholder on error keeps the row readable either way.
 */
function VideoThumb({ video }: { video: VideoReviewItem }) {
    const [failed, setFailed] = useState(false);
    const src = video.thumbnailUrl
        || (video.platform === "youtube" ? getVideoThumbnail(video.videoId, "youtube", "default") : undefined);

    if (!src || failed) {
        return (
            <span className="flex h-full w-full items-center justify-center">
                <Play className="h-3.5 w-3.5 text-stone-400" aria-hidden="true" />
            </span>
        );
    }
    return (
        <img
            src={src}
            alt=""
            className="h-full w-full object-cover"
            loading="lazy"
            onError={() => setFailed(true)}
        />
    );
}

/**
 * Nothing due. Lydi takes the whole surface — no count band, no queue, no
 * tabs. Just the character and one warm line, then a single exit. The corner
 * close stays as the secondary path, so there is no second button repeating
 * what the X already does.
 */
function CelebrationState({
    onClose,
    buttonRef,
}: {
    onClose: () => void;
    buttonRef: RefObject<HTMLButtonElement>;
}) {
    return (
        <div className="relative overflow-hidden bg-gradient-to-br from-stone-50 via-background to-emerald-50/40 dark:from-stone-900/50 dark:via-background dark:to-emerald-950/15">
            <div className="relative flex flex-col items-center px-7 py-10 text-center sm:px-8">
                <motion.div
                    initial={{ scale: 0.7, rotate: -10, opacity: 0 }}
                    animate={{ scale: 1, rotate: 0, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 300, damping: 18, delay: 0.05 }}
                    className="rounded-full bg-white/70 p-2 shadow-sm ring-1 ring-stone-200/70 dark:bg-stone-900/60 dark:ring-stone-800"
                >
                    <Mascot mood="perfect" size="lg" ariaLabel="Lydi is celebrating — you are all caught up." />
                </motion.div>

                <DialogTitle className="mt-5 font-display text-2xl font-semibold tracking-tight text-stone-900 dark:text-stone-50">
                    All caught up
                </DialogTitle>
                <DialogDescription className={cn(typeBodyClass, "mt-2 max-w-[34ch] text-balance")}>
                    Nothing needs your attention right now. Lydi&apos;s proud of you — enjoy the rest of your day.
                </DialogDescription>

                <div className="mt-7 w-full">
                    <button
                        ref={buttonRef}
                        onClick={onClose}
                        className="h-10 w-full rounded-lg bg-primary-strong text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                        Keep it going
                    </button>
                </div>
            </div>
        </div>
    );
};